import { Progress } from '@/components/atoms';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import SellytLogo from '@/assets/svg/sellyt-logo.svg?react';
import { AuthFooter } from '@/pages/auth/components';
import type { OAuthUser } from '@/types';

export type Origin = 'google' | 'email';
export interface OutletContext {
  credentials?: OAuthUser;
  setCredentials: Dispatch<SetStateAction<OAuthUser | undefined>>;
  step: number;
  /** Jump to an absolute onboarding step and keep `location.state.step` in sync. */
  goToStep: (next: number, extraState?: Record<string, unknown>) => void;
  incrementStep: (count?: number) => void;
  decrementStep: (count?: number) => void;
  accessToken?: string;
  setAccessToken: Dispatch<SetStateAction<string | undefined>>;
  origin?: Origin;
  setOrigin: Dispatch<SetStateAction<Origin | undefined>>;
}

export const AuthLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [origin, setOrigin] = useState<Origin>();
  const [step, setStep] = useState(location.state?.step ?? 0);
  const [accessToken, setAccessToken] = useState<string>();
  const [credentials, setCredentials] = useState<OAuthUser>();
  const { pathname } = location;
  const stepRef = useRef(step);
  const prevPathnameRef = useRef(pathname);

  // AuthLayout stays mounted across /login ↔ /sign-up. Sync step from route
  // state, but ignore a stale `location.state.step` while goToStep() is ahead
  // of the in-flight navigate (same pathname).
  useEffect(() => {
    const pathChanged = prevPathnameRef.current !== location.pathname;
    prevPathnameRef.current = location.pathname;

    if (typeof location.state?.step === 'number') {
      if (!pathChanged && location.state.step < stepRef.current) {
        return;
      }
      stepRef.current = location.state.step;
      setStep(location.state.step);
      return;
    }

    if (pathChanged) {
      stepRef.current = 0;
      setStep(0);
    }
  }, [location.pathname, location.state?.step]);

  const totalSteps = pathname.includes('login')
    ? 2
    : pathname.includes('forgot-password')
      ? 4
      : 4;

  const goToStep = useCallback(
    (next: number, extraState?: Record<string, unknown>) => {
      window.scrollTo(0, 0);
      stepRef.current = next;
      setStep(next);
      navigate(
        { pathname: location.pathname, search: location.search },
        {
          replace: true,
          state: {
            ...(typeof location.state === 'object' && location.state
              ? location.state
              : {}),
            ...extraState,
            step: next,
          },
        }
      );
    },
    [navigate, location.pathname, location.search, location.state]
  );

  const incrementStep = useCallback(
    (count?: number) => {
      goToStep(stepRef.current + (count ?? 1));
    },
    [goToStep]
  );

  const decrementStep = useCallback(
    (count?: number) => {
      goToStep(Math.max(0, stepRef.current - (count ?? 1)));
    },
    [goToStep]
  );

  const outletContext: OutletContext = {
    credentials,
    setCredentials,
    step,
    goToStep,
    incrementStep,
    decrementStep,
    accessToken,
    setAccessToken,
    origin,
    setOrigin,
  };

  return (
    <div className="grid grid-cols-2 h-screen">
      <div className="col-span-1 hidden lg:block bg-primary/10 px-4 md:px-6 lg:px-8 py-4 md:py-8 lg:py-10">
        <SellytLogo className="w-18" />
        <div className="flex flex-col gap-10 justify-center items-center">
          <img src="/images/auth-banner.png" alt="" className="w-md" />
          <AuthFooter
            heading="Never Miss a Sale Again, Turn Every Chat into Cash!"
            description="Let Sellyt handle chats, payments, and follow-ups so you can breathe easy, sell more, and watch your business grow."
          />
        </div>
      </div>
      <div className="col-span-2 lg:col-span-1 w-full max-w-2xl pb-6 flex flex-col mx-auto relative">
        <div className="flex flex-col gap-4 px-4 md:px-6 lg:px-8 py-4 md:py-8 lg:py-10 sticky top-0 bg-white">
          <SellytLogo className="w-18 lg:hidden" />
          {step !== 0 && step !== 4 && (
            <Progress variant="step" value={step} totalSteps={totalSteps} />
          )}
        </div>
        <Outlet context={outletContext} />
      </div>
    </div>
  );
};
